import { PermissionType } from "~~/types/user";

export default defineNuxtRouteMiddleware(async (to, from) => {
  // session was explicitly revoked server-side — skip Firebase state check entirely
  // since Firebase signOut is async, auth state may briefly lag behind the revocation
  const authStore = useAuthStore();
  if (authStore.sessionRevoked) {
    return to.path === "/login" ? undefined : navigateTo("/login");
  }

  // Restrict live-tracking domain to only serve /external/* pages
  if (import.meta.client && window.location.hostname === "live-tracking.rsaautonetwork.com") {
    if (!to.path.startsWith("/external")) {
      throw createError({
        statusCode: 404,
        statusMessage: "Page Not Found",
        fatal: true,
      });
    }
    return; // Allow /external/* routes on live-tracking domain
  }

  // Allow public access to /external/* paths
  if (to.path.startsWith("/external")) return;

  // Allow access to recovery pages before a session exists
  if (to.path.startsWith("/auth/recover")) return;

  const { user, allowedRoutes } = await useUserStore().getRouteAuth();

  if (!user && to.path !== "/login") return navigateTo(`/login?destination_url=${to.fullPath}`);

  // Firebase can be signed in during MFA — only leave /login when a refresh session exists.
  if (user && to.path.includes("/login")) {
    const authStore = useAuthStore();
    await authStore.initSession();
    if (authStore.accessToken) return navigateTo("/");
    return;
  }

  // Check if user has ACCESS_WEB_APP permission
  if (user && !user.permissions?.includes(PermissionType.ACCESS_WEB_APP)) {
    if (to.path !== "/access-denied") {
      return navigateTo("/access-denied");
    }
    return; // Allow access to access-denied page
  }

  // If user has ACCESS_WEB_APP permission and is on the access-denied page, redirect to the dashboard
  if (user && user.permissions?.includes(PermissionType.ACCESS_WEB_APP) && to.path === "/access-denied") {
    return navigateTo("/");
  }

  // get the first allowed route name
  const allowedRouteName = Object.keys(allowedRoutes).reduce((acc, routeName) => {
    if (!acc && allowedRoutes[routeName as keyof typeof allowedRoutes]) {
      acc = allowedRoutes[routeName as keyof typeof allowedRoutes] as string;
    }
    return acc;
  }, "" as string | null);

  if (user && to.name && !allowedRoutes.find((routeName) => to.name?.toString()?.includes?.(routeName)))
    if (allowedRouteName) {
      // if there is no allowed route name and at least one allowed route, redirect to the first allowed route
      return navigateTo(`/${allowedRouteName}`);
    } else {
      return abortNavigation("You are not allowed to access this page");
    }
});
